// src/data/services/jasa-pembuatan-software.tsx

"use client";
import { ServiceSection } from "@/components/ServiceSection";
import { site } from "@/data/default";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import Image from "next/image";

// Import Swiper styles
import "swiper/css/pagination";
import "swiper/css";

const JasaPembuatanSoftwareContent = (
  <>
    {/* DESKRIPSI */}
    <ServiceSection id="deskripsi" title="Jasa Pembuatan Software Custom">
      <div className="grid md:grid-cols-2 gap-8 items-center">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4">
            Solusi Software Bisnis <span className="text-purple">Custom & Scalable</span>
          </h1>
          <p className="text-base mb-4">
            Kembangkan bisnis kamu dengan sistem digital berbasis kebutuhanmu. Kami bantu rancang, bangun, dan jalankan software custom tanpa template, cepat, aman, & 100% scalable.
          </p>
          <ul className="text-sm space-y-2 mb-6 text-muted-foreground">
            <li>✅ Sistem ERP, CRM, HRIS, PWA, API Otomasi</li>
            <li>✅ Tim senior developer, UI/UX, dan DevOps</li>
            <li>✅ Garansi hasil, source code milikmu</li>
          </ul>
          <a
            href={`https://wa.me/${site.whatsapp}?text=Halo%20${site.name}%2C%20saya%20butuh%20bantuan%20buat%20software%20custom`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-gradient-to-r from-purple-600 to-orange-500 text-white px-6 py-3 rounded-full font-semibold hover:scale-105 transition"
          >
            🚀 Konsultasi Gratis Sekarang
          </a>
        </div>
        <div className="hidden md:block rounded-xl overflow-hidden">
          <Image
            src="/images/services/software-development.webp"
            alt="Software Custom untuk Bisnis"
            className="w-full max-w-md mx-auto"
            width={1024}
            height={1024}
          />
        </div>
      </div>
    </ServiceSection>

    {/* KEUNGGULAN */}
    <ServiceSection id="keunggulan" title="Kenapa Harus Kami?">
      <div className="grid md:grid-cols-2 gap-6 mt-10">
        {[
          {
            title: "🔍 Berangkat dari Riset",
            desc: "Kami memulai setiap proyek dari observasi kebutuhan & perilaku user secara nyata, bukan asumsi, bukan template generik.",
          },
          {
            title: "🧱 Modular & Scalable",
            desc: "Software kami tidak mati gaya. Dirancang untuk tumbuh, bisa di-upgrade tanpa membongkar ulang fondasi.",
          },
          {
            title: "🤝 Support Mindset",
            desc: "Kami bukan vendor. Kami adalah partner teknologi jangka panjang, siap bantu brainstorming & strategi.",
          },
          {
            title: "🔓 100% Transparan",
            desc: "Source code milikmu. Tidak ada kunci-kuncian. Semua bisa di-deploy ulang, dikembangkan ulang, kapan pun kamu mau.",
          },
        ].map((item, i) => (
          <div
            key={i}
            className="group flex flex-col justify-between p-6 border rounded-xl shadow-md bg-gradient-to-br from-white to-gray-50 hover:from-purple-50 hover:to-white transition-all duration-300 hover:shadow-xl"
          >
            <div className="flex items-start gap-4">
              <div className="text-4xl">{item.title.split(" ")[0]}</div>
              <div>
                <h3 className="text-lg font-semibold  mb-1">
                  {item.title.split(" ").slice(1).join(" ")}
                </h3>
                <p className="text-sm leading-relaxed">{item.desc}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </ServiceSection>

    {/* LAYANAN */}
    <ServiceSection id="layanan" title="Layanan Pengembangan Software">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {[
          {
            title: "Custom Web App",
            tech: ["React", "Next.js", "Laravel", "Node.js"],
            desc: "Aplikasi web tailor-made untuk kebutuhan spesifik: admin panel, sistem booking, atau portal pelanggan.",
          },
          {
            title: "Sistem Informasi Internal",
            tech: ["CRM", "HRIS", "ERP"],
            desc: "Solusi manajemen internal perusahaan yang efisien, terintegrasi, dan siap ekspansi.",
          },
          {
            title: "Dashboard & Analytics",
            tech: ["Realtime", "Chart.js", "Tailwind"],
            desc: "Monitoring performa, laporan otomatis, dan insight berbasis data untuk keputusan bisnis yang akurat.",
          },
          {
            title: "Integrasi API & Otomasi",
            tech: ["WhatsApp", "Email", "Midtrans", "Google Calendar"],
            desc: "Otomatisasi komunikasi & operasional tanpa repot, dari notifikasi sampai sistem pembayaran.",
          },
          {
            title: "Progressive Web App (PWA)",
            tech: ["Service Worker", "Offline Cache"],
            desc: "Aplikasi yang bisa diakses layaknya mobile app tanpa perlu install. Cepat, ringan, dan bisa offline.",
          },
          {
            title: "Cloud Deployment",
            tech: ["VPS", "Docker", "Firebase", "Supabase"],
            desc: "Kami bantu deploy, setup CI/CD, dan memastikan sistemmu siap running 24/7 tanpa downtime.",
          },
        ].map((item, i) => (
          <article
            key={i}
            className="relative group p-6 bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 ease-in-out h-full flex flex-col justify-between"
            itemScope
            itemType="https://schema.org/Service"
          >
            <div>
              <h3 itemProp="name" className="text-xl font-bold  group-hover:text-purple-600 transition-colors">
                {item.title}
              </h3>
              <div className="flex flex-wrap gap-2 mt-3 mb-4">
                {item.tech.map((tech, t) => (
                  <span
                    key={t}
                    className="text-xs font-medium bg-purple-100 text-purple-800 px-2 py-1 rounded-md"
                  >
                    {tech}
                  </span>
                ))}
              </div>
              <p itemProp="description" className="text-sm leading-relaxed">
                {item.desc}
              </p>
            </div>

            <meta itemProp="provider" content="GrasiTech" />
            <meta itemProp="areaServed" content="Indonesia" />
          </article>
        ))}
      </div>
    </ServiceSection>

    {/* STUDIO KASUS */}
    <ServiceSection id="studi-kasus" title="Studi Kasus Nyata">
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        {[
          {
            title: "Laporan Harian Gudang",
            client: "Perusahaan Distribusi Multi-Cabang",
            tech: ["Dashboard Web", "Realtime Data", "Auto Export"],
            result: "Akurasi data meningkat drastis, laporan mingguan kini otomatis hanya dalam hitungan detik.",
          },
          {
            title: "Platform E-Learning Karyawan",
            client: "Perusahaan Manufaktur Nasional",
            tech: ["Next.js", "Firebase", "Sertifikasi Digital"],
            result: "Proses pelatihan 90% lebih cepat. Karyawan bisa belajar kapan saja tanpa kelas fisik.",
          },
          {
            title: "Sistem Order & Notifikasi Otomatis",
            client: "UMKM Produk Skincare",
            tech: ["WhatsApp API", "Notifikasi Invoice", "Frontend Tailwind"],
            result: "Admin lebih efisien, pelanggan langsung dapat invoice otomatis setelah checkout.",
          },
          {
            title: "Sistem Booking Event Venue",
            client: "Startup Penyedia Ruang Acara",
            tech: ["React", "Midtrans", "Calendar API"],
            result: "Booking naik 3x lipat karena proses reservasi lebih cepat, terintegrasi payment gateway & kalender.",
          },
        ].map((item, i) => (
          <div key={i} className="p-5 border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition duration-200">
            <h3 className="text-lg font-semibold mb-1">{item.title}</h3>
            <p className="text-sm text-muted-foreground italic mb-2">{item.client}</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {item.tech.map((tech, t) => (
                <span key={t} className="text-xs bg-gray-100 px-2 py-0.5 rounded-md font-medium">
                  {tech}
                </span>
              ))}
            </div>
            <p className="text-sm">{item.result}</p>
          </div>
        ))}
      </div>
    </ServiceSection>

    {/* PAKET HARGA */}
    <ServiceSection id="harga" title="Harga Transparan, Bisa Disesuaikan">
      <div className="grid md:grid-cols-3 gap-4 my-10">
        {/* STARTUP */}
        <div className="flex flex-col justify-between border border-gray-200 rounded-3xl p-6 shadow-sm hover:shadow-xl hover:scale-[1.02] transition-all duration-300 bg-white">
          <div>
            <h3 className="font-bold mb-2 flex items-center gap-2">💡 Paket Startup</h3>
            <p className="text-3xl font-bold mb-4">Rp8 Juta~</p>
            <ul className="text-sm space-y-2 mb-4">
              <li>✅ MVP / prototipe cepat</li>
              <li>✅ Frontend + Backend ringan</li>
              <li>✅ 1-2 user role</li>
              <li>✅ Estimasi 2-8 minggu</li>
            </ul>
          </div>
          <p className="text-xs italic text-muted-foreground mt-6">
            Cocok untuk validasi ide awal, startup, & UMKM digital.
          </p>
        </div>

        {/* BISNIS - HIGHLIGHTED */}
        <div className="flex flex-col justify-between relative border-2 border-purple-600 rounded-3xl p-6 bg-gradient-to-b from-purple-50 to-white shadow-lg hover:shadow-2xl transition-all duration-300 scale-[1.03] z-10">
          <div className="absolute -top-3 right-4 bg-purple-600 text-white text-xs px-3 py-1 rounded-full shadow">
            ⭐ Best Value
          </div>
          <div>
            <h3 className="font-bold mb-2 flex items-center gap-2 text-purple-700">🚀 Paket Bisnis</h3>
            <p className="text-3xl font-bold text-purple-800 mb-4">Rp20 Juta~</p>
            <ul className="text-sm space-y-2 mb-4 ">
              <li>✅ Sistem internal perusahaan</li>
              <li>✅ Integrasi API (WA, Email, dll)</li>
              <li>✅ 3-5 user role</li>
              <li>✅ Estimasi 4-16 minggu</li>
            </ul>
          </div>
          <p className="text-xs italic text-muted-foreground mt-6">
            Untuk otomasi kerja, monitoring, dan efisiensi operasional.
          </p>
        </div>

        {/* ENTERPRISE */}
        <div className="flex flex-col justify-between border border-gray-200 rounded-3xl p-6 shadow-sm hover:shadow-xl hover:scale-[1.02] transition-all duration-300 bg-white">
          <div>
            <h3 className="font-bold mb-2 flex items-center gap-2">🏢 Paket Enterprise</h3>
            <p className="text-3xl font-bold text-orange-600 mb-4">Rp50 Juta+</p>
            <ul className="text-sm space-y-2 mb-4">
              <li>✅ Sistem kompleks custom 100%</li>
              <li>✅ Microservices / modular</li>
              <li>✅ Integrasi SSO, payment gateway, dsb</li>
              <li>✅ Estimasi fleksibel (6-24 minggu)</li>
            </ul>
          </div>
          <p className="text-xs italic text-muted-foreground mt-6">
            Cocok untuk skala nasional, produk komersial, dan enterprise.
          </p>
        </div>
      </div>

      {/* CTA */}
      <div className="text-center mt-10">
        <p className="text-sm mb-2">Belum yakin paket mana yang cocok?</p>
        <a
          href={`https://wa.me/${site.whatsapp}?text=Halo%20${site.name}%2C%20saya%20mau%20diskusi%20soal%20software%20custom`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block bg-gradient-to-r from-indigo-600 via-purple-600 to-orange-500 text-white text-sm px-6 py-3 rounded-full shadow-lg hover:scale-105 transition"
        >
          🚀 Kirim Ide Sekarang → Kami Bantu Breakdown!
        </a>
      </div>
    </ServiceSection>

    {/* FAQ */}
    <ServiceSection id="faq" title="FAQ - Pertanyaan Umum">
      <div className="space-y-4">
        {[
          {
            question: "Apa bisa request fitur custom?",
            answer:
              "Tentu! Justru itu kekuatan utama kami: membangun software berbasis kebutuhan spesifikmu. Mulai dari dashboard khusus, integrasi API unik, hingga sistem logika bisnis yang kompleks, semua bisa diatur dari awal.",
          },
          {
            question: "Apa saja yang termasuk dalam garansi?",
            answer:
              "Kami memberikan garansi 3 bulan pasca-project. Termasuk: perbaikan bug, penyesuaian minor, dan support teknis. Untuk support lanjutan, tersedia paket berlangganan.",
          },
          {
            question: "Berapa lama waktu pengerjaan software?",
            answer:
              "Durasi pengerjaan tergantung kompleksitas. MVP: 2-8 minggu. Sistem internal: 4-16 minggu. Enterprise: 6-24 minggu. Timeline lengkap diberikan saat kick-off.",
          },
          {
            question: "Apakah source code akan diberikan?",
            answer:
              "Ya. Semua hasil dari source code akan jadi milikmu 100%, lengkap dengan dokumentasi. Kami tidak melakukan vendor lock-in.",
          },
          {
            question: "Bisakah software dikembangkan di masa depan?",
            answer:
              "Tentu. Kami membangun dengan arsitektur modular dan scalable, jadi kamu bisa menambahkan fitur kapan pun tanpa takut sistem rusak.",
          },
        ].map((item, i) => (
          <details
            key={i}
            className="group border border-gray-200 rounded-xl p-4 transition-all open:shadow-sm open:bg-gray-50"
          >
            <summary className="font-semibold text-base cursor-pointer list-none flex justify-between items-center">
              <span>{item.question}</span>
              <span className="ml-2 transform group-open:rotate-180 transition-transform">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="" viewBox="0 0 16 16">
                  <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708" />
                </svg>
              </span>
            </summary>
            <p className="mt-3 text-sm">{item.answer}</p>
          </details>
        ))}
      </div>
    </ServiceSection>

    {/* CLIENT LOGO */}
    <ServiceSection id="client-logo" title="Dipercaya oleh Klien dari Berbagai Industri">
      <div className="mt-6">
        <Swiper
          spaceBetween={20}
          slidesPerView={2}
          breakpoints={{
            640: { slidesPerView: 3 },
            768: { slidesPerView: 4 },
            1024: { slidesPerView: 5 },
          }}
          loop
          autoplay={{ delay: 3000 }}
          className="opacity-80 grayscale hover:grayscale-0 transition-all"
        >
          {[
            "/images/clients/client-1.png",
            "/images/clients/client-2.png",
            "/images/clients/client-3.png",
            "/images/clients/client-4.png",
            "/images/clients/client-5.png",
            "/images/clients/client-6.png",
            "/images/clients/client-7.png",
            "/images/clients/client-8.png",
          ].map((logo, index) => (
            <SwiperSlide key={index}>
              <div className="flex justify-center items-center h-20">
                <img
                  src={logo}
                  alt={`Logo Klien Software Development ${index + 1}`}
                  className="max-h-12 object-contain"
                  loading="lazy"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </ServiceSection>

    {/* TESTIMONI */}
    <ServiceSection id="testimoni" title="Apa Kata Klien Kami?">
      <div className="">
        <Swiper
          spaceBetween={20}
          slidesPerView={1.2}
          breakpoints={{
            768: { slidesPerView: 2.2 },
            1024: { slidesPerView: 3 },
          }}
          autoplay={{ delay: 6000 }}
          loop
        >
          {[
            {
              name: "Dea Kartika",
              job: "Founder Startup Logistik",
              comment:
                "Kami butuh sistem pelacakan armada berbasis web yang real-time. Tim ini bukan cuma coding, mereka mikirin alur bisnisnya juga. Sekarang semua bisa dipantau dalam 1 dashboard—hemat ratusan juta tiap bulan.",
              image: "/images/testimonials/testimony-dea.webp",
            },
            {
              name: "Haryo Wibowo",
              job: "Direktur Keuangan Holding Company",
              comment:
                "Software laporan keuangan custom yang mereka bangun bener² ngubah cara kami kerja. Semua otomatis—dari approval, multi cabang, sampai integrasi pajak. Hemat waktu, akurasi naik. Mereka ngerti banget kompleksitas korporasi.",
              image: "/images/testimonials/testimony-haryo.webp",
            },
            {
              name: "Nadya Ramadhani",
              job: "Owner Klinik Kecantikan",
              comment:
                "Dulu, jadwal treatment & database pasien masih pakai Excel. Sekarang semua digital, terhubung ke WhatsApp dan payment gateway. Bukan cuma software, ini cara baru untuk scale bisnis saya tanpa buka cabang baru.",
              image: "/images/testimonials/testimony-nadya.webp",
            },
            {
              name: "Andika Prasetyo",
              job: "Kepala Sekolah SMA Swasta",
              comment:
                "Kami minta sistem e-learning berbasis web yang bisa diakses dari HP guru² di daerah. Bukan cuma bisa diakses, tapi bahkan auto-generate raport & absensi. Sekolah kami jadi rujukan nasional sekarang.",
              image: "/images/testimonials/testimony-andika.webp",
            },
            {
              name: "Tari Meilani",
              job: "Manajer Operasional Perusahaan Ritel",
              comment:
                "Setiap bulan saya stres input stok dari 14 cabang. Sekarang, semua sudah pakai software inventory realtime, bahkan notifikasi auto masuk ke Telegram saya. Worth every penny, ROI-nya gila.",
              image: "/images/testimonials/testimony-tari.webp",
            },
            {
              name: "Rangga Maulana",
              job: "Owner Bisnis Franchise Makanan",
              comment:
                "Sistem kasir dan manajemen waralaba yang mereka buat bikin ekspansi jadi jauh lebih gampang. Semua mitra saya bisa onboarding & monitoring mandiri. Saya bisa fokus branding, bukan urus data lagi.",
              image: "/images/testimonials/testimony-rangga.webp",
            },
          ].map((item, i) => (
            <SwiperSlide key={i} className="h-full flex self-stretch mb-4">
              <div className="bg-white border rounded-xl shadow-sm p-5 flex flex-col justify-between hover:shadow-md transition-all">
                <p className="text-sm leading-relaxed italic mb-4">"{item.comment}"</p>
                <div className="flex items-center gap-3">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-sm font-semibold">{item.name}</p>
                    <p className="text-xs text-muted-foreground">{item.job}</p>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </ServiceSection>


    {/* KONTAK */}
    <ServiceSection id="kontak" title="Diskusi Proyek Software">
      <div className="grid md:grid-cols-2 gap-4 items-center mt-6">
        {/* Kiri: Text Persuasif */}
        <div>
          <h3 className="text-2xl font-bold mb-4">
            Punya ide software, tapi belum tahu mulai dari mana?
          </h3>
          <p className="text-base mb-4">
            Biarkan kami bantu rumuskan solusinya. Tanpa biaya. Tanpa komitmen.
            Cukup ceritakan masalah, proses, atau mimpi digitalmu, kami akan breakdown kebutuhanmu jadi roadmap teknis yang bisa langsung dieksekusi.
          </p>
          <ul className="text-sm space-y-1 mb-6 text-muted-foreground">
            <li>✅ Gratis konsultasi teknis langsung via WhatsApp</li>
            <li>✅ Rekomendasi teknologi paling efisien & scalable</li>
            <li>✅ Estimasi waktu + harga terukur</li>
          </ul>
          <a
            href={`https://wa.me/${site.whatsapp}?text=Halo%20${site.name}%2C%20saya%20butuh%20bantuan%20buat%20software%20custom%20untuk%20...`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center bg-green text-white px-6 py-3 rounded-xl font-semibold hover:bg-green/90 transition"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="text-white me-2" viewBox="0 0 16 16">
              <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
            </svg>
            Konsultasi Sekarang
          </a>
          <p className="text-xs mt-3 ">Kami aktif setiap hari, 09:00-22:00 WIB</p>
        </div>

        {/* Kanan: Visual Map + Trust */}
        <div className="relative w-full h-64 md:h-full rounded-xl overflow-hidden">
          <iframe
            src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3965.8420521856533!2d106.99524857499078!3d-6.284483493704425!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e698d6d29d17095%3A0x765e1cdf4db9b9bb!2sGratia%20Global%20Solution!5e0!3m2!1sid!2sid!4v1754466163988!5m2!1sid!2sid`}
            width="100%"
            height="100%"
            allowFullScreen
            loading="lazy"
            className="border-0 w-full h-full"
            title="Lokasi Kantor"
          ></iframe>
          <div className="absolute bottom-4 left-4 bg-white/90 text-sm px-4 py-2 rounded shadow">
            📍 Berbasis di Bekasi - Jakarta, melayani seluruh Indonesia & luar negeri
          </div>
        </div>
      </div>
    </ServiceSection>

  </>
);

export default JasaPembuatanSoftwareContent;